import React, { useCallback } from 'react';
import { observer } from 'mobx-react';
import cls from 'classnames';
import { useToggle } from '@cutwise/react-utils';

import CollectionPopover from '@/collection/components/CollectionPopover/CollectionPopover';
import Collection from '@/collection/entity/Collection';
import { useCollectionsStore } from '@/collection/stores/CollectionsStore';
import Button from '@/common/components/Button/Button';
import FancyPopup from '@/common/components/FancyPopup/FancyPopup';
import Icon from '@/common/components/Icon/Icon';
import Label, { SIZE_MEDIUM, TERTIARY } from '@/common/components/Label/Label';
import Link, { NONE } from '@/common/components/Link/Link';
import URLProvider from '@/common/services/URLProvider';

import headerStyles from '../Header.module.css';

function CollectionNavItem() {
  const [isOpen, open, close] = useToggle();

  const { activeCollection, toggleActive } = useCollectionsStore();

  function onChangePopupState(isNextOpened: boolean) {
    if (isNextOpened) {
      open();
    } else {
      close();
    }
  }

  const stopPropagation = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handlePinClick = useCallback(
    (e: React.MouseEvent, c: Collection) => {
      e.preventDefault();
      e.stopPropagation();

      toggleActive(c);
    },
    [toggleActive],
  );

  return (
    <React.Fragment>
      {activeCollection && (
        <div aria-live="assertive" className={headerStyles.pinnedCollection}>
          <span className={headerStyles.pinnedCollectionLabel}>Pinned Collection:</span>

          <Link
            aria-label="Pinned collection title"
            appearance={NONE}
            className={headerStyles.pinnedCollectionLink}
            onClick={stopPropagation}
            to={URLProvider.collectionDetailPage(activeCollection)}
          >
            {activeCollection.title}
          </Link>

          <Button
            aria-label="Unpin collection"
            active
            onClick={(e: React.MouseEvent) => handlePinClick(e, activeCollection)}
            size="xs"
            withIcon
          >
            <Icon name="pin-on" />
          </Button>
        </div>
      )}

      <FancyPopup
        action="click"
        isOpen={isOpen}
        onChangeState={onChangePopupState}
        onClickOutside={close}
        placement="bottom"
        usePortal
        isPlain
        body={<CollectionPopover withLinks onClose={close} />}
        trigger={(
          <span
            aria-label="Collection modal toggler"
            className={cls(headerStyles.headerLink, headerStyles.headerLinkIcon, headerStyles.headerLinkCollections)}
          >
            <Icon name="like" />

            {activeCollection && activeCollection?.products.length > 0 && (
              <Label
                className={headerStyles.headerLinkLabel}
                appearance={TERTIARY}
                aria-label="Number of products in pinned collection"
                aria-live="assertive"
                rounded
                size={SIZE_MEDIUM}
              >
                {activeCollection.products.length}
              </Label>
            )}
          </span>
        )}
      />
    </React.Fragment>
  );
}

export default observer(CollectionNavItem);
